import { Box, Grid } from '@mui/material';
import { observer, useLocalObservable } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CardView from '../../component/card/card-view';
import TableComponent, { HeadCell, TableRowFeatures } from '../../component/table/table.component';
import { TopBarState } from '../../component/top-bar/top-bar-state';
import i18n from '../../i18n';
import { MobxObjectsManager, useManualRemove } from '../mobx-objs/mobx-manager';
// scss
import './scss/tunnel-management.style.scss';
import TunnelManagementMobx from './tunnel-management-state';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { RestaurantTunnelData } from '../../models/restaurant.entity';
import ButtonComponent from '../../component/button/button.component';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { ConditionType, SearchKeyInput, SearchKeyInputItems } from '../../component/form/search-key-input.component';
import { toJS } from 'mobx';
import { Reseller } from '../../models';
import _ from 'lodash';
import { getResellerFilter } from '../../constants/tool.reseller';

/**
 * 餐厅隧道管理列表页
 * @returns
 */
const TunnelManagementPage = () => {

    useTranslation();

    // 使用数据管理
    const useData = useLocalObservable(() => new TunnelManagementMobx());

    const { tunnelLists, isTableLoading, conditions } = useData;

    useEffect(() => {
        useData.init();
        const topbarState = MobxObjectsManager.get(TopBarState);
        topbarState.updateTunnelManageTitle();

        return () => {
        }
    }, []);

    useManualRemove(TunnelManagementMobx);


    // *********************
    // Attributes
    // *********************

    // 表格顶部下拉框数组
    let selectFilterLists: SearchKeyInputItems[] = [
        {
            value: 'nickname',
            label: i18n.t('restaurant_management_page_table_text_restaurant_name'),
            type: 'TEXT'
        }, {
            value: 'string_id',
            label: i18n.t('restaurant_page_input_text_restaurant_identification_code'),
            type: 'TEXT'
        }, {
            value: 'database_ports',
            label: i18n.t('port'),
            type: 'TEXT'
        }
    ];
    selectFilterLists = getResellerFilter(selectFilterLists);

    // 表格头部
    const tableHeadCell: HeadCell[] = [
        {
            id: 'belonged_reseller', label: i18n.t('reseller_page_text_reseller'), renderFunction: (row: RestaurantTunnelData) => {
                const reseller = _.get(row, 'belonged_reseller_info.[0]') as Reseller | undefined;
                return reseller ? `${reseller.first_name} ${reseller.last_name}` : 'Admin';
            }
        },
        {
            id: 'group_name', label: i18n.t('general_text_belonged_group'), sortStatus: useData.getSortStatus('group_name'),
            renderFunction: (row: RestaurantTunnelData) => {
                return <>{row?.belonged_group_info?.nickname || ''}</>
            }
        },
        {
            id: 'nickname', label: i18n.t('restaurant_management_page_table_text_restaurant_name'), sortStatus: useData.getSortStatus('nickname'),
        },
        {
            id: 'string_id', label: i18n.t('restaurant_page_input_text_restaurant_identification_code'),
            // renderFunction: (row) => {
            //     return <></>
            // }
        },
        {
            id: 'city', label: i18n.t('restaurant_management_page_table_text_city'),
            // renderFunction: (row) => {
            //     return <></>
            // }
        },
        {
            id: 'database_host', label: i18n.t('db address'),
            renderFunction: (row) => {
                return <>{row?.database_host || ''}</>
            }
        },
        {
            id: 'database_port', label: i18n.t('port'), sortStatus: useData.getSortStatus('database_port'),
            renderFunction: (row) => {
                return <>{useData.formatDatabasePortText(row?.database_port, row?.project)}</>
            }
        },
        {
            id: 'action_bar', label: i18n.t('general_text_action_bar'),
            renderFunction: (row: RestaurantTunnelData) => {
                return renderFeatures(row);
            }
        },
    ];

    /**
     * table里面的功能键
     * @param row 行数据
     * @param index 行索引
     * @returns
     */
    const renderFeatures = (row: RestaurantTunnelData): React.ReactNode => {
        return (
            <TableRowFeatures
                buttons={[
                    {
                        variant: 'contained',
                        color: 'primary',
                        titleText: i18n.t('general_text_download'),
                        children: <FileDownloadIcon />,
                        handleClick: () => {
                            // TODO: 下载隧道文件
                            useData.downloadFile(row)
                        }
                    }
                ]}
            />
        )
    }

    // *********************
    // Extra Function
    // *********************

    // 渲染页面组件
    const renderContent = (): JSX.Element => {
        return (
            <Box className={'container'}>
                <Grid container>
                    <Grid item xs={12}>
                        <Box className={'top-tool'}>
                            <SearchKeyInput
                                items={selectFilterLists as SearchKeyInputItems[]}
                                conditions={toJS(conditions)}
                                onSearch={(conditions: ConditionType[]) => {
                                    useData.setConditions(conditions);
                                }}
                                showConditionsBox
                            />
                            <ButtonComponent
                                {...{
                                    variant: 'contained',
                                    size: 'small',
                                    extraButtonStyle: 'add-button',
                                    titleText: `${i18n.t('tunnel_management_page_export')}`,
                                    children: <CloudDownloadIcon />,
                                    async: true,
                                    space: false,
                                    handleClick: async () => {
                                        await useData.downloadTunnelExcel();
                                    }
                                }}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <TableComponent
                            {...{
                                showChecked: 0,
                                list: tunnelLists ?? [],
                                // 开启表格自定义页脚
                                isUseCustomPagination: true,
                                headCellList: tableHeadCell,
                                isTableLoading,
                                handleSort: (headCell) => {
                                    useData.handleSort(headCell);
                                },
                                isPagination: true,
                                allCount: tunnelLists.length,
                                isUseFold: true,
                                extraClass: 'extraTable',
                                compareSymbol:''
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
        )
    }

    return (
        <Box className={'tunnel-management-page'}>
            <Box sx={{}}>
                <CardView
                    {...{
                        className: '',
                        contentRender: renderContent()
                    }}
                />
            </Box>
        </Box>
    )
}

export default observer(TunnelManagementPage);